import { useMemo, useState } from "react";

/** What a settings editor reports to its page so the page can drive the save bar. */
export type EditorStatus = {
    hasChanges: boolean;
    loading: boolean;
    saving: boolean;
    save?: () => Promise<void>;
    discard?: () => void;
};

export const idleEditor: EditorStatus = { hasChanges: false, loading: false, saving: false };

/** Collects the status of one or more editors on a settings page into one save bar. */
export function useEditorStatuses<K extends string>(keys: readonly K[]) {
    const [statuses, setStatuses] = useState<Partial<Record<K, EditorStatus>>>({});
    const keyList = keys.join(" ");
    // One stable reporter per editor, so editors can list it as an effect dependency.
    const report = useMemo(() => Object.fromEntries(keyList.split(" ").map(key => [key, (status: EditorStatus) =>
        setStatuses(current => ({ ...current, [key]: status }))])) as Record<K, (status: EditorStatus) => void>, [keyList]);
    const all = keys.map(key => statuses[key] ?? idleEditor);
    const changed = all.filter(status => status.hasChanges);
    return {
        report,
        form: {
            dirty: changed.length > 0,
            saving: all.some(status => status.saving),
            onSave: () => { void Promise.all(changed.map(status => status.save?.())).catch(() => undefined); },
            onDiscard: () => changed.forEach(status => status.discard?.()),
        },
    };
}
